import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormatDueDate } from '@/lib/i18n/useDateTimeFormatters'

interface ActivityLike {
  activityType: string
  fieldName?: string | null
  description?: string | null
  oldValue?: string | null
  newValue?: string | null
}

const parseIsoDate = (value: string | null | undefined): Date | undefined => {
  if (!value) return undefined
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

/**
 * Render a task activity as a locale-aware sentence from structured fields.
 * Falls back to `activity.description` for unknown activity types so we stay
 * forwards-compatible with whatever the server logs.
 */
export function useActivitySentence() {
  const { t } = useTranslation('activity')
  const formatDueDate = useFormatDueDate()

  return useCallback(
    (activity: ActivityLike): string => {
      const type = activity.activityType
      const fallback = activity.description ?? ''

      const formatDate = (value: string | null | undefined) => {
        const parsed = parseIsoDate(value)
        if (!parsed) return ''
        return formatDueDate(parsed, { includeRelative: false })
      }

      switch (type) {
        case 'created':
          return t('created', { name: activity.newValue ?? '' })

        case 'completed':
          return t('completed')

        case 'reopened':
          return t('reopened')

        case 'status_changed':
          // oldValue / newValue are status IDs; the server-rendered
          // description already has resolved names, so use it as-is until
          // we thread a status lookup through.
          return fallback

        case 'priority_changed': {
          const oldKey = (activity.oldValue ?? '').trim()
          const newKey = (activity.newValue ?? '').trim()
          const priorityLabel = (key: string) =>
            (key && t(`priority.${key}`, { defaultValue: key })) || t('none')
          if (oldKey === '0' && newKey !== '0' && newKey !== '')
            return t('prioritySet', { newValue: priorityLabel(newKey) })
          if (oldKey !== '0' && oldKey !== '' && newKey === '0')
            return t('priorityCleared')
          return t('priorityChanged', {
            oldValue: priorityLabel(oldKey),
            newValue: priorityLabel(newKey),
          })
        }

        case 'name_changed':
          return t('nameChanged', {
            oldValue: activity.oldValue ?? '',
            newValue: activity.newValue ?? '',
          })

        case 'description_changed':
          return t('descriptionChanged')

        case 'assigned':
        case 'unassigned':
        case 'assignee_changed':
          // oldValue / newValue hold user IDs; description already carries
          // resolved names.
          return fallback

        case 'due_date_changed': {
          const oldDate = formatDate(activity.oldValue)
          const newDate = formatDate(activity.newValue)
          if (!activity.oldValue && activity.newValue)
            return t('dueDateSet', { newDate })
          if (activity.oldValue && !activity.newValue) return t('dueDateCleared')
          return t('dueDateChanged', { oldDate, newDate })
        }

        case 'start_date_changed': {
          const oldDate = formatDate(activity.oldValue)
          const newDate = formatDate(activity.newValue)
          if (!activity.oldValue && activity.newValue)
            return t('startDateSet', { newDate })
          if (activity.oldValue && !activity.newValue)
            return t('startDateCleared')
          return t('startDateChanged', { oldDate, newDate })
        }

        case 'parent_changed':
        case 'project_changed':
          // oldValue / newValue hold IDs; description already carries names.
          return fallback

        case 'label_added':
          return t('labelAdded', {
            newValue: activity.newValue ?? fallback,
          })

        case 'label_removed':
          return t('labelRemoved', {
            oldValue: activity.oldValue ?? fallback,
          })

        default:
          // Unknown activity type — use the server-provided description.
          return fallback
      }
    },
    [t, formatDueDate],
  )
}
